import type { LoaderFunctionArgs } from "@remix-run/node";
import { json } from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
  isRouteErrorResponse,
  useLocation,} from "@remix-run/react";
import { captureRemixErrorBoundaryError } from "@sentry/remix";
import { detectLocale } from "~/utils/locale";
import { useNonce } from "~/utils/nonce-provider";
import { optionalRetailAuth } from "~/services/auth/retailSession.server";
import {
  getCustomerIdentity,
  getGuestIdentity,
  getOrCreateVisitorId,
} from "~/services/facebook/customerIdentity.server";
import {
  normalizeCity,
  normalizeState,
  normalizeName,
  normalizeZip,
  normalizeCountry,
  normalizeEmail,
  normalizePhone,
} from "~/utils/normalizeForMeta";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const cookieHeader = request.headers.get("Cookie") || "";
  const match = cookieHeader.match(/(?:^|;\s*)eql_theme=([^;]*)/);
  const theme = match?.[1] || "light";
  // T1.2: detect locale server-side so <html lang> matches the language the user
  // actually sees. Required for WCAG 3.1.1 and for SEO on the Vietnamese market.
  const locale = detectLocale(request.url, cookieHeader);

  // P1.11 — noindex on the staging domain. Until we flip DNS to
  // diolo.com, Google can happily index test.diolo.com and
  // split ranking signals between the two origins. `host` below comes
  // from the incoming Host header (Cloudflare forwards it unchanged),
  // so this turns off automatically post-cutover without any manual
  // toggle.
  const host = new URL(request.url).hostname.toLowerCase();
  const isStaging =
    host === "test.diolo.com" || host.endsWith(".fly.dev");

  // Audit 2026-05-06 (compliance, item #12): cookie consent gate.
  // Until the visitor explicitly accepts, non-essential analytics
  // scripts (GA4, Clarity, Meta Pixel) are NOT rendered. The
  // `eql_cookie_consent` cookie is set client-side by CookieConsent.tsx.
  const consentMatch = cookieHeader.match(/(?:^|;\s*)eql_cookie_consent=([^;]*)/);
  const cookieConsent = consentMatch?.[1] || null;
  const analyticsAllowed = cookieConsent === "accepted";

  // Audit 2026-05-16 (v2 — push toward 9+/10 EMQ): Manual Advanced
  // Matching for Meta Pixel, expanded to include city/state/zip from
  // the customer's default address and a persistent eql_visitor_id
  // cookie that serves as external_id for guests.
  //
  // Previous version (commit 5b2b1ea) sent em/ph/fn/ln/external_id/country
  // and pushed logged-in EMQ from 3.0 to ~6/10. Adding ct/st/zp + a
  // stable guest external_id pushes it further:
  //   - logged-in: ~6/10 → ~8-9/10 (extra address fields)
  //   - guests: ~3/10 → ~5-6/10 (now has a stable external_id even
  //     before they ever log in or buy — Meta can join sessions)
  //
  // Why call optionalRetailAuth in the ROOT loader (it runs for every
  // request including /admin and /portal): the retail_token cookie is
  // only set for retail customers, so optionalRetailAuth returns null
  // immediately (~free) when the cookie is absent. Tiny DB roundtrip
  // only when a real retail customer is browsing. Wrapped in catch()
  // so any auth-layer failure never breaks the root layout.
  const retailAuth = analyticsAllowed
    ? await optionalRetailAuth(request).catch(() => null)
    : null;

  // Visitor ID — persistent 1y cookie minted on first analytics-consented
  // visit and reused thereafter. Only set under cookie consent (the
  // helper itself is policy-agnostic; we gate the call). Used as the
  // external_id when no logged-in customer is present — gives Meta a
  // stable cross-session handle for guests.
  let visitorId: string | null = null;
  let visitorSetCookie: string | null = null;
  if (analyticsAllowed) {
    const v = getOrCreateVisitorId(cookieHeader);
    visitorId = v.visitorId;
    visitorSetCookie = v.setCookieHeader;
  }

  const facebookAM: Record<string, string> = {};
  if (retailAuth?.customer) {
    // Pull the full identity (customer + default-address join) via the
    // shared helper so the AM object stays in lockstep with what CAPI
    // sends from api.fb-capi.ts / checkout.tsx.
    const id = await getCustomerIdentity(retailAuth.customer.id).catch(
      () => ({} as Awaited<ReturnType<typeof getCustomerIdentity>>),
    );
    // Audit 2026-05-17: switched to shared normalizers so the raw
    // strings the Pixel SDK SHA256-hashes client-side match BYTE-FOR-BYTE
    // what conversionsApi.server.ts hashes server-side. Previously the
    // browser path ignored Vietnamese diacritics (e.g. "Hà Nội" was
    // sent as "hànội" while CAPI hashed "ha noi" → server+browser
    // never deduped against Meta's reference identity).
    const em = normalizeEmail(id.email);              if (em) facebookAM.em = em;
    const ph = normalizePhone(id.phone);              if (ph) facebookAM.ph = ph;
    const fn = normalizeName(id.firstName);           if (fn) facebookAM.fn = fn;
    const ln = normalizeName(id.lastName);            if (ln) facebookAM.ln = ln;
    const ct = normalizeCity(id.city);                if (ct) facebookAM.ct = ct;
    const st = normalizeState(id.state);              if (st) facebookAM.st = st;
    const zp = normalizeZip(id.zipCode);              if (zp) facebookAM.zp = zp;
    const co = normalizeCountry(id.country);          if (co) facebookAM.country = co;
    if (id.externalId) facebookAM.external_id = id.externalId;
  } else if (visitorId) {
    // Guest with consent: start with visitor_id + country as the
    // anonymous baseline (gives Meta a stable cross-session handle).
    facebookAM.external_id = visitorId;
    facebookAM.country = "vn";
    // Then promote them to "phone-known" if they previously submitted
    // the phone-capture modal. Only worth the DB roundtrip when the
    // visitor cookie was ALREADY set (i.e. not a brand-new session).
    if (!visitorSetCookie) {
      const guestLead = await getGuestIdentity(visitorId).catch(
        () => ({}) as Awaited<ReturnType<typeof getGuestIdentity>>,
      );
      if (guestLead.phone) {
        const ph = normalizePhone(guestLead.phone);
        if (ph) facebookAM.ph = ph;
      }
      if (guestLead.email) {
        const em = normalizeEmail(guestLead.email);
        if (em) facebookAM.em = em;
      }
    }
  }

  return json({
    theme,
    locale,
    isStaging,
    analyticsAllowed,
    facebookAM,
    ENV: {
      SENTRY_DSN: process.env.SENTRY_DSN || "",
      FACEBOOK_PIXEL_ID: process.env.FACEBOOK_PIXEL_ID || "",
      // Set via `flyctl secrets set GOOGLE_SITE_VERIFICATION=...` after
      // creating the GSC property and copying the meta-tag token. Empty
      // string = no tag rendered, so this is safe to leave blank.
      GOOGLE_SITE_VERIFICATION: process.env.GOOGLE_SITE_VERIFICATION || "",
      // Microsoft Clarity project ID — heatmaps + session recordings.
      // Created at https://clarity.microsoft.com, Project settings → Setup.
      // `flyctl secrets set CLARITY_PROJECT_ID=abc123def` on production.
      CLARITY_PROJECT_ID: process.env.CLARITY_PROJECT_ID || "",
      // GA4 measurement ID (G-XXXXXXX). `flyctl secrets set GA4_MEASUREMENT_ID=...`
      GA4_MEASUREMENT_ID: process.env.GA4_MEASUREMENT_ID || "",
    },
  }, visitorSetCookie ? { headers: { "Set-Cookie": visitorSetCookie } } : undefined);
};

export default function App() {
  const { theme, locale, ENV, isStaging, analyticsAllowed, facebookAM } = useLoaderData<typeof loader>();
  // CSP nonce — used by every inline <script> below + <Scripts /> below.
  // See app/utils/nonce-provider.tsx + app/entry.server.tsx for the
  // request-scoped value source.
  const nonce = useNonce();
  // The staff phone app (/staff) is an installable PWA — its manifest and iOS
  // home-screen tags only belong on those pages.
  const isStaffApp = useLocation().pathname.startsWith("/staff");
  // Resolve server-side: if "system", default to light (client will fix on hydration)
  const serverDataTheme = theme === "dark" ? "dark" : undefined;

  return (
    // suppressHydrationWarning on <html>: the inline FOUC theme script in
    // <head> below mutates `data-theme` BEFORE React hydrates (reads
    // localStorage + matchMedia, neither of which the server has access to).
    // Server may render <html> with no data-theme; client may have it set
    // to "dark" by the time React hydrates. That's a deliberate divergence
    // for FOUC prevention — without this attribute React 18 logged a
    // hydration mismatch on every page load, which contributed to the
    // 22+ #418 cluster that broke form-event hydration on /account/login
    // and /account/register. (Audit 2026-04-25 P0 fix.)
    <html lang={locale} translate="no" suppressHydrationWarning {...(serverDataTheme ? { "data-theme": serverDataTheme } : {})}>
      <head>
        <meta charSet="utf-8" />
        {/* Do NOT set `maximum-scale=1` — it disables user zoom, failing WCAG 2.1 AA
            success criterion 1.4.4 (Resize Text). Users with low vision must be able
            to magnify content. */}
        {/* viewport-fit=cover lets layout extend into iOS safe-area zones
            (notch / home indicator) so we can use env(safe-area-inset-*)
            for sticky elements. Audit 2026-05-06. */}
        <meta name="viewport" content="width=device-width,initial-scale=1,viewport-fit=cover" />
        {/* Audit 2026-05-14 (Clarity #418/#423 hydration mismatch hunt):
            We serve content in the customer's chosen locale (VI or EN, via
            the eql_locale cookie + LocaleSelectModal). Chrome/Safari/Edge
            mobile auto-translate then mutates the DOM BEFORE React hydrates
            — every translated text node breaks hydration with React error
            #418 ("text content did not match"). Clarity 7-day data:
            83% of 511 JS errors were #418, concentrated on VN-mobile.
            `notranslate` tells the browser's translate UI to skip the
            page. Customers who want a different language use OUR built-in
            locale switcher (header globe icon → toggles to EN/VI). */}
        <meta name="google" content="notranslate" />
        {isStaffApp && (
          <>
            <link rel="manifest" href="/staff.webmanifest" />
            <link rel="apple-touch-icon" href="/staff/icon-180.png" />
            <meta name="apple-mobile-web-app-capable" content="yes" />
            <meta name="apple-mobile-web-app-title" content="Diolo Staff" />
            <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
            <meta name="theme-color" content="#000000" />
          </>
        )}
        {/* Audit 2026-05-15: iOS Safari + iOS WebViews (Instagram /
            Facebook / Google in-app browsers) ALSO mutate the DOM
            before React hydrates via "data detectors" — phone numbers,
            dates, addresses, and emails in our HTML get auto-wrapped
            in `<a>` tags. Same hydration-mismatch failure mode as
            Chrome's auto-translate, but `notranslate` doesn't disable
            data detectors. `format-detection` is the iOS-specific
            opt-out per Apple's docs, and Clarity confirmed 18 of the
            19 #418-triggering sessions today were iOS browsers (the
            single Chrome desktop session was admin-only). Pairs with
            `notranslate` above — together they cover the two known
            pre-hydration DOM mutation vectors on this storefront. */}
        <meta
          name="format-detection"
          content="telephone=no, date=no, address=no, email=no"
        />
        {/* Keep staging out of Google's index while we share the domain. */}
        {isStaging ? (
          <meta name="robots" content="noindex, nofollow" />
        ) : null}
        {/* Google Search Console verification — only emits when env is set. */}
        {ENV.GOOGLE_SITE_VERIFICATION ? (
          <meta
            name="google-site-verification"
            content={ENV.GOOGLE_SITE_VERIFICATION}
          />
        ) : null}
        {/* Favicon — the D of the wordmark, white on black, cut from Hayden's
            logo export (public/brand/diolo-logo.svg, 2026-09-06). Until then
            this was the OLD D pulled from the Shopify file. `?v=diolo2` busts
            the icon cache — browsers hold favicons for weeks otherwise. */}
        <link rel="icon" type="image/svg+xml" href="/favicon.svg?v=diolo2" />
        <link rel="icon" type="image/png" href="/favicon.png?v=diolo2" sizes="512x512" />
        <link rel="icon" href="/favicon.ico?v=diolo2" sizes="16x16 32x32 48x48" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png?v=diolo2" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        {/* Audit 2026-05-13 (Clarity CLS=0.48 — POOR): swapped
            `display=swap` → `display=optional`. With swap, the browser
            renders system-font fallback first, then re-renders the
            page with DM Sans once it arrives — every such swap is a
            layout shift contributing to CLS. With `optional`, the
            browser uses the fallback IF the font isn't ready in
            ~100ms, and then NEVER swaps mid-pageload. Trade-off: on
            slow connections the visitor sees fallback fonts for that
            page load (cached on subsequent pageloads). Acceptable for
            CLS-zero. Preconnect + preload below keep the happy path
            fast enough that most visitors see DM Sans on first paint. */}
        {/* Diolo rebrand 2026-06-10: single neo-grotesque family across the
            storefront (menswear-activewear identity — tight tracking, title
            case, no serif). Inter Tight's compact metrics are the closest
            open-licensed match to the target look. */}
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Inter+Tight:ital,wght@0,400;0,500;0,600;0,700;1,400&display=optional"
        />
        {/* Inline script to prevent FOUC: applies dark mode before paint */}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `(function(){try{var t=document.cookie.match(/(?:^|;\\s*)eql_theme=([^;]*)/);var v=t?t[1]:localStorage.getItem("eql_theme")||"light";var r=v==="dark"?"dark":v==="system"&&window.matchMedia("(prefers-color-scheme:dark)").matches?"dark":"light";if(r==="dark")document.documentElement.setAttribute("data-theme","dark");else document.documentElement.removeAttribute("data-theme");}catch(e){}})();`,
          }}
        />
        {/* ─── Google Analytics 4 (gtag.js) — only if GA4_MEASUREMENT_ID is set
            AND the visitor has accepted analytics cookies (audit 2026-05-06). */}
        {analyticsAllowed && ENV.GA4_MEASUREMENT_ID && (
          <>
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${ENV.GA4_MEASUREMENT_ID}`}
              nonce={nonce}
            />
            <script
              nonce={nonce}
              dangerouslySetInnerHTML={{
                __html: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${ENV.GA4_MEASUREMENT_ID}',{anonymize_ip:true});`,
              }}
            />
          </>
        )}
        {/* ─── Microsoft Clarity — only if CLARITY_PROJECT_ID is set ───
            Audit 2026-04-26: split into two tags. Previously this was a
            single inline script that did `document.createElement("script")
            + parentNode.insertBefore(t, y)` synchronously — which mutates
            the <head> DOM BEFORE React hydrates. React then walks the
            DOM, finds extra <script> nodes that aren't in its virtual
            tree, and logs ~22 hydration-mismatch errors (#418) across
            <head> children plus a #423 recovery on <body>. Real-browser
            test on 2026-04-25 reproduced this site-wide.
            By rendering the loader <script src=...> tag declaratively
            via React, it's part of the virtual tree → SSR HTML matches
            → no mismatch. The init <script> just sets up the queue
            function (no DOM injection) so it's safe inline. */}
        {analyticsAllowed && ENV.CLARITY_PROJECT_ID && (
          <>
            <script
              nonce={nonce}
              dangerouslySetInnerHTML={{
                __html: `window.clarity=window.clarity||function(){(window.clarity.q=window.clarity.q||[]).push(arguments)};`,
              }}
            />
            <script
              async
              src={`https://www.clarity.ms/tag/${ENV.CLARITY_PROJECT_ID}`}
              nonce={nonce}
            />
          </>
        )}
        {/* ─── Meta Pixel base code (only if FACEBOOK_PIXEL_ID is set) ───
            Same fix as Clarity above: split the loader <script src> out
            of the inline init code so React owns the script tag and no
            DOM injection happens before hydration. The init script
            populates the fbq queue, sets up `init` and `PageView`, all
            of which queue commands until the loader script downloads.

            Audit 2026-05-16, two enrichments wired into this script:
              1. fbclid → _fbc cookie capture (belt-and-braces backup for
                 Meta's auto-capture, which can miss on slow connections,
                 CSP edge cases, or when fbevents.js loads after the user
                 has already navigated away from the fbclid landing URL).
                 Cookie format per Meta spec: fb.1.{unix_ms}.{fbclid}.
                 Skipped if _fbc is already set.
              2. Manual Advanced Matching object passed as the 2nd arg to
                 fbq('init', PIXEL_ID, {em, ph, fn, ln, external_id,
                 country}). Pixel SDK SHA256-hashes these client-side
                 before sending to Meta. Lifts PageView EMQ for any
                 logged-in customer from 3.0/10 → ~7/10 (the previous
                 Automatic AM only caught 4% via DOM scanning). Object
                 is JSON-stringified server-side; values are sanitized
                 already in the loader. JSON.stringify on plain object
                 values is safe to inline inside <script> as long as
                 values don't contain "</script>" — defensively
                 escaping "<" → "\\u003c" anyway. */}
        {analyticsAllowed && ENV.FACEBOOK_PIXEL_ID && (
          <>
            <script
              nonce={nonce}
              dangerouslySetInnerHTML={{
                // Audit 2026-05-17: REMOVED the inline `fbq('track','PageView')`
                // call from this init script. PageView is now fired from
                // usePageviewTracker (client-side useEffect) with a
                // session-unique event_id that is mirrored to /api/fb-capi
                // server-side for full identity enrichment + dedup. Keeping
                // the inline call here would have caused double-counting
                // (Meta would see one PageView from auto-fire + another
                // from useEffect with mismatched event_ids → 2 events).
                // The noscript <img> fallback below still covers no-JS
                // visitors with a server-side PageView ping.
                __html: `(function(){try{var u=new URLSearchParams(window.location.search);var f=u.get('fbclid');if(!f)return;if(document.cookie.indexOf('_fbc=')>=0)return;document.cookie='_fbc=fb.1.'+Date.now()+'.'+f+'; path=/; max-age=7776000; SameSite=Lax';}catch(e){}})();!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[]}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${ENV.FACEBOOK_PIXEL_ID}'${Object.keys(facebookAM).length > 0 ? `,${JSON.stringify(facebookAM).replace(/</g, "\\u003c")}` : ""});`,
              }}
            />
            <script
              async
              src="https://connect.facebook.net/en_US/fbevents.js"
              nonce={nonce}
            />
          </>
        )}
        <Meta />
        <Links />
      </head>
      <body>
        {/* Pixel no-JS fallback lives in <body>: an <img> inside <noscript>
            in <head> is invalid HTML — the browser hoists it out, React's
            hydration walk then mismatches on every page (#418 → #423). */}
        {analyticsAllowed && ENV.FACEBOOK_PIXEL_ID && (
          <noscript>
            <img
              height="1"
              width="1"
              style={{ display: "none" }}
              src={`https://www.facebook.com/tr?id=${ENV.FACEBOOK_PIXEL_ID}&ev=PageView&noscript=1`}
              alt=""
            />
          </noscript>
        )}
        <Outlet />
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(ENV)};`,
          }}
        />
        {/*
         * T3.35: Web Vitals beacon. We use the native PerformanceObserver
         * API to capture LCP, CLS, and TTFB (no `web-vitals` npm package
         * needed, keeping bundle size flat). Metrics are sent with
         * navigator.sendBeacon so they survive page unloads. The backend
         * endpoint is /api/web-vitals which already persists to the DB.
         */}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `
(function(){
  try {
    var deviceType = /Mobi|Android|iPhone/i.test(navigator.userAgent) ? "mobile" : "desktop";
    function rate(name, v) {
      if (name === "LCP") return v <= 2500 ? "good" : v <= 4000 ? "needs-improvement" : "poor";
      if (name === "CLS") return v <= 0.1 ? "good" : v <= 0.25 ? "needs-improvement" : "poor";
      if (name === "TTFB") return v <= 800 ? "good" : v <= 1800 ? "needs-improvement" : "poor";
      // INP (Interaction to Next Paint) replaced FID as the responsiveness
      // Core Web Vital in March 2024. Google thresholds: good ≤ 200ms,
      // needs-improvement ≤ 500ms, poor > 500ms.
      if (name === "INP") return v <= 200 ? "good" : v <= 500 ? "needs-improvement" : "poor";
      return "good";
    }
    function send(name, value) {
      try {
        var body = JSON.stringify({
          name: name,
          value: value,
          rating: rate(name, value),
          pathname: location.pathname,
          deviceType: deviceType
        });
        if (navigator.sendBeacon) navigator.sendBeacon("/api/web-vitals", body);
        else fetch("/api/web-vitals", { method: "POST", body: body, keepalive: true, headers: { "Content-Type": "application/json" } });
      } catch(e) {}
    }
    // LCP — take the largest entry before page hide
    if ("PerformanceObserver" in window) {
      var lcpValue = 0;
      try {
        var lcpObs = new PerformanceObserver(function(list) {
          var entries = list.getEntries();
          var last = entries[entries.length - 1];
          if (last) lcpValue = last.startTime;
        });
        lcpObs.observe({ type: "largest-contentful-paint", buffered: true });
      } catch(e) {}
      // CLS — accumulate session-scoped layout shifts
      var clsValue = 0;
      try {
        var clsObs = new PerformanceObserver(function(list) {
          list.getEntries().forEach(function(entry) {
            if (!entry.hadRecentInput) clsValue += entry.value;
          });
        });
        clsObs.observe({ type: "layout-shift", buffered: true });
      } catch(e) {}
      // INP (Interaction to Next Paint) — Google new responsiveness CWV
      // since March 2024 (replaced FID). We track the WORST interaction
      // duration across the session, which is what Google own field-
      // data tooling does. event-timing entries with interactionId > 0
      // are user interactions (clicks, key presses); duration is the
      // time from input to next paint. Audit 2026-05-17: added because
      // our prod telemetry showed LCP CLS TTFB but missing INP -- could
      // not measure mobile jank. Now we can.
      var inpValue = 0;
      try {
        var inpObs = new PerformanceObserver(function(list) {
          list.getEntries().forEach(function(entry) {
            if (entry.interactionId && entry.duration > inpValue) {
              inpValue = entry.duration;
            }
          });
        });
        inpObs.observe({ type: "event", buffered: true, durationThreshold: 16 });
      } catch(e) {}
      // Flush on page hide so we capture final LCP + CLS + INP
      addEventListener("visibilitychange", function() {
        if (document.visibilityState === "hidden") {
          if (lcpValue > 0) send("LCP", Math.round(lcpValue));
          send("CLS", Math.round(clsValue * 1000) / 1000);
          if (inpValue > 0) send("INP", Math.round(inpValue));
        }
      }, { once: true });
    }
    // TTFB — from navigation timing
    try {
      var navEntry = performance.getEntriesByType && performance.getEntriesByType("navigation")[0];
      if (navEntry && navEntry.responseStart > 0) {
        send("TTFB", Math.round(navEntry.responseStart));
      }
    } catch(e) {}
  } catch(e) {}
})();
            `.trim(),
          }}
        />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  // T1.2: ErrorBoundary runs before the loader resolves, so we can't use the
  // same dynamic locale detection as <App />. Defaulting to "vi" since the VN
  // market is the primary audience; English fallback is acceptable on the
  // rare occasion an error fires before locale detection.
  //
  // Branded 500 vs 404 messaging: the storefront catch-all route
  // (_storefront.$.tsx) has its own 404 page so most 404s never reach this
  // ErrorBoundary. When they do (e.g. /admin/* 404), we still want a
  // reasonable brand-consistent fallback rather than Remix's stock error.
  const isRouteErr = isRouteErrorResponse(error);
  const status = isRouteErr ? error.status : 500;
  const is404 = status === 404;
  const heading = is404 ? "404 — Trang không tồn tại" : "Đã có lỗi xảy ra";
  const subheading = is404
    ? "Page not found"
    : "Something went wrong on our end.";
  const message = is404
    ? "Trang bạn tìm kiếm không tồn tại hoặc đã bị xoá. Vui lòng kiểm tra lại đường dẫn hoặc quay về trang chủ."
    : "Chúng tôi đã ghi nhận sự cố và đang xử lý. Vui lòng thử lại sau hoặc liên hệ với chúng tôi nếu cần trợ giúp ngay.";
  const messageEn = is404
    ? "The page you're looking for doesn't exist or has been moved. Check the URL or head back to the homepage."
    : "We've logged the issue and are looking into it. Please try again, or contact us if you need immediate help.";
  return (
    <html lang="vi">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>{status} | Diolo</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=optional"
        />
      </head>
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          fontFamily: '"DM Sans", system-ui, sans-serif',
          backgroundColor: "#fafaf8",
          color: "#1a1a1a",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header
          style={{
            padding: "1.25rem 1.5rem",
            borderBottom: "1px solid #eaeaea",
            backgroundColor: "#ffffff",
          }}
        >
          <a
            href="/"
            style={{
              fontSize: "1rem",
              fontWeight: 600,
              letterSpacing: "0.15em",
              textDecoration: "none",
              color: "#1a1a1a",
              textTransform: "uppercase",
            }}
          >
            Diolo
          </a>
        </header>
        <main
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem 1.5rem",
          }}
        >
          <div style={{ maxWidth: 520, textAlign: "center" }}>
            <p
              style={{
                fontSize: "0.6875rem",
                fontWeight: 500,
                letterSpacing: "0.2em",
                color: "#8a8a8a",
                marginBottom: "1.25rem",
                textTransform: "uppercase",
              }}
            >
              {status} {subheading}
            </p>
            <h1
              style={{
                fontSize: "1.75rem",
                fontWeight: 500,
                letterSpacing: "-0.01em",
                marginBottom: "1rem",
                lineHeight: 1.2,
              }}
            >
              {heading}
            </h1>
            <p style={{ color: "#555", lineHeight: 1.55, marginBottom: "0.5rem" }}>
              {message}
            </p>
            <p style={{ color: "#888", lineHeight: 1.55, fontSize: "0.875rem", marginBottom: "2rem" }}>
              {messageEn}
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: "0.75rem", flexWrap: "wrap" }}>
              <a
                href="/"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "#1a1a1a",
                  color: "#fff",
                  textDecoration: "none",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                Về trang chủ
              </a>
              <a
                href="/products"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "transparent",
                  color: "#1a1a1a",
                  textDecoration: "none",
                  border: "1px solid #1a1a1a",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                Cửa hàng
              </a>
              <a
                href="/contact"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "0.75rem 1.5rem",
                  backgroundColor: "transparent",
                  color: "#555",
                  textDecoration: "none",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                Liên hệ
              </a>
            </div>
          </div>
        </main>
      </body>
    </html>
  );
}
